import { useMemo, useState, createContext, useContext } from 'react';
import { AuthProvider as AuthProviderOIDC } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { LOCALSTORAGE_LAST_VISITED } from '@constants/login';
import { DEFAULT_LOCALE } from '../../../state/domain/Locale';

const AuthConfigContext = createContext();

export function useAuthConfig() {
  return useContext(AuthConfigContext);
}

function AuthProvider({ children, getKeyCloakConfigFromStorage }) {
  const { keyCloakConfig = null } = getKeyCloakConfigFromStorage();
  const [oidcConfig, setOidcConfig] = useState(keyCloakConfig);

  const value = useMemo(
    () => ({ oidcConfig, setOidcConfig }),
    [oidcConfig, setOidcConfig],
  );

  return (
    <AuthConfigContext.Provider value={value}>
      {oidcConfig ? (
        <AuthProviderOIDC
          {...oidcConfig}
          automaticSilentRenew
          userStore={new WebStorageStateStore({ store: window.localStorage })}>
          {children}
        </AuthProviderOIDC>
      ) : (
        children
      )}
    </AuthConfigContext.Provider>
  );
}

/**
 * Clears Keycloak/OIDC tokens from localStorage. Call this on logout.
 */
export function clearAuthTokens(callback, store) {
  if (!callback) {
    throw new Error('Callback must be defined in clear token');
  }
  const lastUser = localStorage.getItem(LOCALSTORAGE_LAST_VISITED) || '';
  const localeSnapshot = JSON.stringify({
    locale: store?.domain?.locale?.get?.() ?? DEFAULT_LOCALE,
    localesSupported: store?.domain?.locale?.getSupportedLocales?.() ?? [],
  });

  window.localStorage.clear();
  window.sessionStorage.clear();

  setTimeout(() => {}, 300);
  localStorage.setItem(LOCALSTORAGE_LAST_VISITED, lastUser || '');
  localStorage.setItem('LocaleStore', localeSnapshot);
  callback();
}

export default AuthProvider;
